import Navbar from '@/components/Navbar'
import {Rocket, Users, TrendingUp, Binoculars, Twitter, Send} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

function Home() {
  const [visibleSections, setVisibleSections] = useState<Set<string>>(new Set())
  const [showMoonmapModal, setShowMoonmapModal] = useState(false)
  const observerRef = useRef<IntersectionObserver | null>(null)

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => new Set(prev).add(entry.target.id))
          }
        })
      },
      { threshold: 0.1 }
    )

    document.querySelectorAll('section[id]').forEach((section) => {
      observerRef.current?.observe(section)
    })

    return () => observerRef.current?.disconnect()
  }, [])

  return (
    <div className="min-h-screen text-white relative overflow-hidden" style={{ fontFamily: '"Space Grotesk", sans-serif', background: '#000000' }}>
      {/* Animated Stars Background with Shooting Stars */}
      <div className="fixed inset-0 pointer-events-none" style={{ zIndex: 0 }}>
        {Array.from({ length: 30 }).map((_, i) => {
          const size = Math.random() * 3 + 1;
          const duration = Math.random() * 3 + 2;
          const delay = Math.random() * 2;
          return (
            <div
              key={i}
              className="absolute bg-white rounded-full"
              style={{
                width: size + 'px',
                height: size + 'px',
                top: Math.random() * 100 + '%',
                left: Math.random() * 100 + '%',
                animation: `twinkle ${duration}s ease-in-out ${delay}s infinite`,
                boxShadow: '0 0 4px rgba(255,255,255,0.8)'
              }}
            />
          );
        })}
        {/* Shooting Stars */}
        {Array.from({ length: 3 }).map((_, i) => (
          <div
            key={`shooting-${i}`}
            className="absolute h-0.5 bg-gradient-to-r from-transparent via-white to-transparent"
            style={{
              width: '100px',
              top: Math.random() * 50 + '%',
              left: '-100px',
              animation: `shoot ${8 + i * 3}s linear ${i * 5}s infinite`,
              transform: 'rotate(-45deg)'
            }}
          />
        ))}
        <style>{`
          @keyframes twinkle {
            0%, 100% { opacity: 0.3; }
            50% { opacity: 1; }
          }
        `}</style>
      </div>
      
      <div className="relative" style={{ zIndex: 1 }}>
        <Navbar />

        {/* Hero Section */}
        <section id="home" className="pt-32 pb-32 px-4">
          <div className="max-w-7xl mx-auto text-center">
            <div className="mb-8 flex justify-center">
              <img
                src="https://static.lumi.new/23/235fe20a59deb0d45df2c70b1103e26a.webp"
                alt="Rocket Bunny Astronaut and Moon Animated"
                className="h-72 w-auto"
                loading="eager"
                style={{
                  animation: 'bunnyFly 3s ease-in-out infinite'
                }}
              />
              <style>{`
                @keyframes bunnyFly {
                  0%, 100% {
                    transform: translateY(0) rotate(-2deg);
                  }
                  25% {
                    transform: translateY(-20px) rotate(1deg);
                  }
                  50% {
                    transform: translateY(-30px) rotate(-1deg);
                  }
                  75% {
                    transform: translateY(-20px) rotate(1deg);
                  }
                }
              `}</style>
            </div>
            <h1 className="text-6xl md:text-8xl font-bold mb-6 text-white" style={{ fontFamily: '"Fredoka One", cursive', textShadow: '0 0 40px rgba(239, 68, 68, 0.6)' }}>
              ROCKET BUNNY
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-gray-300" style={{ fontWeight: 500 }}>
              🚀 To The Moon and Beyond! 🐰
            </p>
            <p className="text-lg md:text-xl mb-12 max-w-3xl mx-auto text-gray-400">
              Join the cutest memecoin revolution in the galaxy. 
              Rocket Bunny is hopping straight to the moon with its community-driven mission!
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <a
                href="https://dexscreener.com/solana/jaenm1y4dhp9lmxzg7yjaw9inqxjgfmaxpfhoyvsvwfr"
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-4 bg-transparent border-2 text-white font-bold text-lg rounded hover:scale-105 transition-all duration-300 relative group"
                style={{ borderColor: '#EF4444' }}
              >
                <span className="relative z-10">View Chart</span>
                <div className="absolute inset-0 bg-gradient-to-r from-red-600/10 to-orange-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </a>
              <a
                href="https://solscan.io/token/UXWRXMN14kZ4zD6NFZYnBHvR1wc5sC6xnWuBJADpump"
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-4 bg-transparent border-2 text-white font-bold text-lg rounded hover:scale-105 transition-all duration-300 relative group"
                style={{ borderColor: '#EF4444' }}
              >
                <span className="relative z-10">Solscan</span>
                <div className="absolute inset-0 bg-gradient-to-r from-red-600/10 to-orange-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </a>
              <a
                href="https://pump.fun/coin/UXWRXMN14kZ4zD6NFZYnBHvR1wc5sC6xnWuBJADpump"
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-4 bg-transparent border-2 text-white font-bold text-lg rounded hover:scale-105 transition-all duration-300 relative group"
                style={{ borderColor: '#EF4444' }}
              >
                <span className="relative z-10">Pump.Fun</span>
                <div className="absolute inset-0 bg-gradient-to-r from-red-600/10 to-orange-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </a>
            </div>
          </div>
        </section>

        {/* About Section */}
        <section id="about" className="py-32 px-4">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-bold text-center mb-20" style={{ fontFamily: '"Fredoka One", cursive', opacity: visibleSections.has('about') ? 1 : 0, transform: visibleSections.has('about') ? 'translateY(0)' : 'translateY(30px)', transition: 'all 0.8s ease-out' }}>
              About Rocket Bunny
            </h2>
            <div className="max-w-4xl mx-auto">
              <div
                className="p-8 md:p-12 border-2 rounded-lg relative overflow-hidden"
                style={{
                  borderColor: '#EF4444',
                  opacity: visibleSections.has('about') ? 1 : 0,
                  transform: visibleSections.has('about') ? 'translateY(0)' : 'translateY(30px)',
                  transition: 'all 0.8s ease-out 0.2s',
                  background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(249, 115, 22, 0.05) 100%)',
                  boxShadow: '0 0 40px rgba(239, 68, 68, 0.2)'
                }}
              >
                <p className="text-lg md:text-xl leading-relaxed text-gray-300" style={{ lineHeight: '1.8' }}>
                  In the heart of the bustling Solana memeverse, a small fluffy bunny sat under a velvet night sky, gazing at the glowing moon and whispering, "One day… I'm going to strap on a rocket and hop all the way up there." Skeptical voices laughed that bunnies belong on the ground and slow rugs would pull him back, but he carried pure hopium — a sparkling belief that heart and friends could overcome anything. So he slipped on rocket boots, rallied believers tired of rugs, and blasted off on Solana. That dreamer became $RBUNNY: a wholesome, community-first ecosystem escaping slow rugs and loading hopium together with pure vibes and zero toxicity. $RBUNNY isn't just a token — it's the story of a tiny bunny who dared to look up and the crew proving even the smallest dreamer can reach the stars. If you feel that same spark, hop in and strap tight — we're lifting off to the moon and beyond right now. <span className="text-red-400 font-bold">Just a bunny with a dream.</span>
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Tokenomics Section */}
        <section id="tokenomics" className="py-32 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="flex justify-center mb-8">
              <div className="relative" style={{ perspective: '1000px' }}>
                <div
                  className="relative"
                  style={{
                    width: '256px',
                    height: '256px',
                    transformStyle: 'preserve-3d',
                    animation: 'coinFlip 4s ease-in-out infinite'
                  }}
                >
                  {/* Front of coin */}
                  <img
                    src="https://static.lumi.new/b7/b7086a74347b5e19629cfe61326f7a1d.webp"
                    alt="Rocket Bunny Coin Front"
                    className="absolute inset-0 w-full h-full rounded-full"
                    style={{
                      backfaceVisibility: 'hidden',
                      WebkitBackfaceVisibility: 'hidden',
                      boxShadow: '0 0 40px rgba(239, 68, 68, 0.6)'
                    }}
                  />
                  {/* Back of coin */}
                  <img
                    src="https://static.lumi.new/04/042d6b942f834b9e3dd0f50389921d31.webp"
                    alt="Rocket Bunny Coin Back"
                    className="absolute inset-0 w-full h-full rounded-full"
                    style={{
                      backfaceVisibility: 'hidden',
                      WebkitBackfaceVisibility: 'hidden',
                      transform: 'rotateY(180deg)',
                      boxShadow: '0 0 40px rgba(239, 68, 68, 0.6)'
                    }}
                  />
                </div>
              </div>
              <style>{`
                @keyframes coinFlip {
                  0% {
                    transform: rotateY(0deg);
                  }
                  50% {
                    transform: rotateY(180deg);
                  }
                  100% {
                    transform: rotateY(360deg);
                  }
                }
              `}</style>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-center mb-20" style={{ fontFamily: '"Fredoka One", cursive', opacity: visibleSections.has('tokenomics') ? 1 : 0, transform: visibleSections.has('tokenomics') ? 'translateY(0)' : 'translateY(30px)', transition: 'all 0.8s ease-out' }}>
              Tokenomics
            </h2>
            <div className="max-w-4xl mx-auto" style={{ opacity: visibleSections.has('tokenomics') ? 1 : 0, transform: visibleSections.has('tokenomics') ? 'translateY(0)' : 'translateY(30px)', transition: 'all 0.8s ease-out 0.3s' }}>
              <div className="border-2 rounded-lg p-8 relative overflow-hidden group" style={{ borderColor: '#EF4444', background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(249, 115, 22, 0.1) 100%)', boxShadow: '0 0 40px rgba(239, 68, 68, 0.3)' }}>
                <div className="space-y-8 text-center">
                  <div>
                    <p className="text-gray-400 mb-3 text-lg">Total Supply</p>
                    <p className="text-4xl font-bold text-red-500">620,000,000</p>
                    <p className="text-gray-500 mt-1">620 Million Tokens</p>
                  </div>
                  <div>
                    <p className="text-gray-400 mb-3 text-lg">Tax</p>
                    <p className="text-3xl font-bold text-orange-500">0% Buy/Sell</p>
                  </div>
                  <div>
                    <p className="text-gray-400 mb-3 text-lg">Liquidity Pool</p>
                    <p className="text-3xl font-bold text-red-500">🔥 Burned</p>
                  </div>
                  <div>
                    <p className="text-gray-400 mb-3 text-lg">Contract Address</p>
                    <div className="flex items-center justify-center gap-3">
                      <p className="text-sm md:text-base font-mono text-red-500 break-all">UXWRXMN14kZ4zD6NFZYnBHvR1wc5sC6xnWuBJADpump</p>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText('UXWRXMN14kZ4zD6NFZYnBHvR1wc5sC6xnWuBJADpump')
                          alert('Contract address copied to clipboard!')
                        }}
                        className="px-4 py-2 bg-red-600 hover:bg-red-700 rounded text-white font-bold text-sm transition-all duration-300 hover:scale-105"
                        title="Copy to clipboard"
                      >
                        Copy
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Token Allocation - Modern Futuristic Donut Charts */}
              <div className="mt-16 border-2 rounded-lg p-8 relative overflow-hidden" style={{ borderColor: '#EF4444', background: 'rgba(0, 0, 0, 0.5)', boxShadow: '0 0 40px rgba(239, 68, 68, 0.3)' }}>
                <h3 className="text-3xl font-bold text-center mb-4" style={{ fontFamily: '"Fredoka One", cursive' }}>
                  Token Allocation
                </h3>
                <p className="text-center text-gray-400 mb-12">Initial Supply: 2,000,000,000 Tokens</p>
                
                {/* Modern Circular Progress Rings */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-5xl mx-auto">
                  {/* Incinerated - 55% */}
                  <div className="flex flex-col items-center group">
                    <div className="relative" style={{ width: '140px', height: '140px' }}>
                      <svg viewBox="0 0 140 140" className="transform -rotate-90">
                        <defs>
                          <linearGradient id="redRingGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor="#EF4444" />
                            <stop offset="100%" stopColor="#DC2626" />
                          </linearGradient>
                        </defs>
                        {/* Background ring */}
                        <circle cx="70" cy="70" r="60" fill="none" stroke="rgba(239, 68, 68, 0.1)" strokeWidth="12" />
                        {/* Progress ring - 65% */}
                        <circle
                          cx="70" cy="70" r="60"
                          fill="none"
                          stroke="url(#redRingGrad)"
                          strokeWidth="12"
                          strokeLinecap="round"
                          strokeDasharray="377"
                          strokeDashoffset="116.87"
                          style={{
                            filter: 'drop-shadow(0 0 8px rgba(239, 68, 68, 0.6))',
                            transition: 'stroke-dashoffset 1s ease-out'
                          }}
                        />
                        {/* Inner glow circle */}
                        <circle cx="70" cy="70" r="50" fill="rgba(239, 68, 68, 0.05)" />
                      </svg>
                      <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <p className="text-3xl font-bold text-red-500" style={{ fontFamily: '"Fredoka One", cursive' }}>69%</p>
                        <p className="text-xs text-gray-500 mt-1">🔥</p>
                      </div>
                    </div>
                    <p className="text-white font-bold mt-3 text-center">Incinerated</p>
                    <p className="text-gray-400 text-xs text-center">1.38B tokens</p>
                  </div>

                  {/* Locked - 15% */}
                  <div className="flex flex-col items-center group">
                    <div className="relative" style={{ width: '140px', height: '140px' }}>
                      <svg viewBox="0 0 140 140" className="transform -rotate-90">
                        <defs>
                          <linearGradient id="orangeRingGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor="#F97316" />
                            <stop offset="100%" stopColor="#EA580C" />
                          </linearGradient>
                        </defs>
                        {/* Background ring */}
                        <circle cx="70" cy="70" r="60" fill="none" stroke="rgba(249, 115, 22, 0.1)" strokeWidth="12" />
                        {/* Progress ring - 15% */}
                        <circle
                          cx="70" cy="70" r="60"
                          fill="none"
                          stroke="url(#orangeRingGrad)"
                          strokeWidth="12"
                          strokeLinecap="round"
                          strokeDasharray="377"
                          strokeDashoffset="320.45"
                          style={{
                            filter: 'drop-shadow(0 0 8px rgba(249, 115, 22, 0.6))',
                            transition: 'stroke-dashoffset 1s ease-out'
                          }}
                        />
                        {/* Inner glow circle */}
                        <circle cx="70" cy="70" r="50" fill="rgba(249, 115, 22, 0.05)" />
                      </svg>
                      <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <p className="text-3xl font-bold text-orange-500" style={{ fontFamily: '"Fredoka One", cursive' }}>15%</p>
                        <p className="text-xs text-gray-500 mt-1">🔒</p>
                      </div>
                    </div>
                    <p className="text-white font-bold mt-3 text-center">Locked</p>
                    <p className="text-gray-400 text-xs text-center">300M tokens</p>
                  </div>

                  {/* Development - 5.9% */}
                  <div className="flex flex-col items-center group">
                    <div className="relative" style={{ width: '140px', height: '140px' }}>
                      <svg viewBox="0 0 140 140" className="transform -rotate-90">
                        <defs>
                          <linearGradient id="yellowRingGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor="#FCD34D" />
                            <stop offset="100%" stopColor="#F59E0B" />
                          </linearGradient>
                        </defs>
                        {/* Background ring */}
                        <circle cx="70" cy="70" r="60" fill="none" stroke="rgba(252, 211, 77, 0.1)" strokeWidth="12" />
                        {/* Progress ring - 5.9% */}
                        <circle
                          cx="70" cy="70" r="60"
                          fill="none"
                          stroke="url(#yellowRingGrad)"
                          strokeWidth="12"
                          strokeLinecap="round"
                          strokeDasharray="377"
                          strokeDashoffset="373.46"
                          style={{
                            filter: 'drop-shadow(0 0 8px rgba(245, 158, 11, 0.6))',
                            transition: 'stroke-dashoffset 1s ease-out'
                          }}
                        />
                        {/* Inner glow circle */}
                        <circle cx="70" cy="70" r="50" fill="rgba(252, 211, 77, 0.05)" />
                      </svg>
                      <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <p className="text-3xl font-bold text-yellow-400" style={{ fontFamily: '"Fredoka One", cursive' }}>0.94%</p>
                        <p className="text-xs text-gray-500 mt-1">🛠️</p>
                      </div>
                    </div>
                    <p className="text-white font-bold mt-3 text-center">Development</p>
                    <p className="text-gray-400 text-xs text-center">18.87M tokens</p>
                  </div>

                  {/* Open Market - 14.1% */}
                  <div className="flex flex-col items-center group">
                    <div className="relative" style={{ width: '140px', height: '140px' }}>
                      <svg viewBox="0 0 140 140" className="transform -rotate-90">
                        <defs>
                          <linearGradient id="whiteRingGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor="#FFFFFF" />
                            <stop offset="100%" stopColor="#D1D1D1" />
                          </linearGradient>
                        </defs>
                        {/* Background ring */}
                        <circle cx="70" cy="70" r="60" fill="none" stroke="rgba(255, 255, 255, 0.1)" strokeWidth="12" />
                        {/* Progress ring - 14.1% */}
                        <circle
                          cx="70" cy="70" r="60"
                          fill="none"
                          stroke="url(#whiteRingGrad)"
                          strokeWidth="12"
                          strokeLinecap="round"
                          strokeDasharray="377"
                          strokeDashoffset="320.22"
                          style={{
                            filter: 'drop-shadow(0 0 8px rgba(255, 255, 255, 0.5))',
                            transition: 'stroke-dashoffset 1s ease-out'
                          }}
                        />
                        {/* Inner glow circle */}
                        <circle cx="70" cy="70" r="50" fill="rgba(255, 255, 255, 0.03)" />
                      </svg>
                      <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <p className="text-3xl font-bold text-white" style={{ fontFamily: '"Fredoka One", cursive' }}>15.06%</p>
                        <p className="text-xs text-gray-500 mt-1">📈</p>
                      </div>
                    </div>
                    <p className="text-white font-bold mt-3 text-center">Open Market</p>
                    <p className="text-gray-400 text-xs text-center">301.13M tokens</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Roadmap Section */}
        <section id="roadmap" className="py-32 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="flex justify-center mb-8">
              <img
                src="https://static.lumi.new/3f/3fe93e2b429768e85a2914d60d07bdfb.webp"
                alt="Rocket Bunny Astronaut"
                className="h-64 w-auto"
                style={{
                  animation: 'moonBounce 3.5s ease-in-out infinite'
                }}
              />
              <style>{`
                @keyframes moonBounce {
                  0%, 100% {
                    transform: translateY(0);
                  }
                  50% {
                    transform: translateY(-30px);
                  }
                }
              `}</style>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-center mb-6" style={{ fontFamily: '"Fredoka One", cursive', opacity: visibleSections.has('roadmap') ? 1 : 0, transform: visibleSections.has('roadmap') ? 'translateY(0)' : 'translateY(30px)', transition: 'all 0.8s ease-out' }}>
              Moonmap
            </h2>
            <div className="text-center mb-20">
              <button
                onClick={() => setShowMoonmapModal(true)}
                className="text-red-500 hover:text-red-400 font-bold text-lg underline hover:scale-105 transition-all duration-300"
                style={{ textShadow: '0 0 10px rgba(239, 68, 68, 0.5)' }}
              >
                Click to View Moonmap
              </button>
            </div>
            <div className="max-w-4xl mx-auto space-y-6">
              {[
                {
                  phase: 'Phase 1',
                  title: 'Launch',
                  items: [
                    { text: 'Token Launch on Pump Fun', completed: true },
                    { text: 'Community Building', completed: true },
                    { text: 'Website Launch', completed: true },
                    { text: 'Social Media Presence', completed: true },
                    { text: 'Token Burn', completed: true },
                    { text: 'Rocket Bunny Game Beta Test', completed: true },
                    { text: 'Meme Contests', completed: true },
                    { text: 'Token Staking', completed: true },
                    { text: 'Airdrop Campaign', completed: false },
                    { text: 'Release CoinHop Exchange', completed: false }
                  ]
                },
                {
                  phase: 'Phase 2',
                  title: 'Growth',
                  items: ['CMC & CG Listings', '5,000 Holders', 'Partnerships', 'Marketing Campaign']
                },
                {
                  phase: 'Phase 3',
                  title: 'Expansion',
                  items: ['CEX Listings', '10,000 Holders', 'Merch Store', 'Community Events']
                },
                {
                  phase: 'Phase 4',
                  title: 'Moon',
                  items: ['Major Exchange Listings', 'Global Domination', 'Rocket Bunny NFTs', 'To The Moon! 🚀']
                }
              ].map((phase, index) => (
                <div
                  key={index}
                  className="border-2 rounded-lg p-6 hover:scale-[1.02] transition-all duration-300 relative group"
                  style={{
                    borderColor: '#EF4444',
                    opacity: visibleSections.has('roadmap') ? 1 : 0,
                    transform: visibleSections.has('roadmap') ? 'translateY(0)' : 'translateY(30px)',
                    transition: `all 0.8s ease-out ${index * 0.15}s`,
                    background: index % 2 === 0 ? 'rgba(239, 68, 68, 0.1)' : 'rgba(249, 115, 22, 0.05)'
                  }}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-red-600/0 via-red-600/5 to-orange-600/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="flex items-start gap-4 relative z-10">
                    <div className="text-red-500 font-bold text-xl whitespace-nowrap">
                      {phase.phase}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold mb-4">{phase.title}</h3>
                      <ul className="space-y-2">
                        {phase.items.map((item, i) => (
                          <li key={i} className="flex items-center gap-2 text-gray-300">
                            <span className={typeof item === 'string' ? 'text-orange-500' : (item.completed ? 'text-green-500' : 'text-orange-500')}>✓</span>
                            {typeof item === 'string' ? item : item.text}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Ecosystem Section */}
        <section id="ecosystem" className="py-32 px-4">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-bold text-center mb-20" style={{ fontFamily: '"Fredoka One", cursive', opacity: visibleSections.has('ecosystem') ? 1 : 0, transform: visibleSections.has('ecosystem') ? 'translateY(0)' : 'translateY(30px)', transition: 'all 0.8s ease-out' }}>
              Ecosystem
            </h2>
            <div className="flex flex-wrap justify-center gap-12">
              <div className="text-center" style={{ opacity: visibleSections.has('ecosystem') ? 1 : 0, transform: visibleSections.has('ecosystem') ? 'translateY(0)' : 'translateY(30px)', transition: 'all 0.8s ease-out 0.2s' }}>
                <a
                  href="https://play.RocketBunnySol.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block cursor-pointer"
                >
                  <img
                    src="https://static.lumi.new/f0/f0efcd00507fb8d77434f36fde09d79c.webp"
                    alt="Rocket Bunny Play 2 Burn Game"
                    className="h-48 w-auto mx-auto hover:scale-110 transition-transform duration-300"
                    style={{ filter: 'drop-shadow(0 0 20px rgba(239, 68, 68, 0.6))' }}
                  />
                </a>
              </div>
              <div className="text-center" style={{ opacity: visibleSections.has('ecosystem') ? 1 : 0, transform: visibleSections.has('ecosystem') ? 'translateY(0)' : 'translateY(30px)', transition: 'all 0.8s ease-out 0.4s' }}>
                <a
                  href="https://app.streamflow.finance/staking/solana/mainnet/3PyYYqy8vZRprKSJJqK5n5qkqBcTi36QHuk4BdWCgxbX"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block cursor-pointer"
                >
                  <img
                    src="https://static.lumi.new/02/02af77ea3658dede636d7d40c22a3f43.webp"
                    alt="$RBUNNY Staking"
                    className="h-64 w-auto mx-auto hover:scale-110 transition-transform duration-300"
                    style={{ filter: 'drop-shadow(0 0 20px rgba(239, 68, 68, 0.6))' }}
                  />
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Community Section */}
        <section id="community" className="py-32 px-4">
          <div className="max-w-7xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-8" style={{ fontFamily: '"Fredoka One", cursive', opacity: visibleSections.has('community') ? 1 : 0, transform: visibleSections.has('community') ? 'translateY(0)' : 'translateY(30px)', transition: 'all 0.8s ease-out' }}>
              Join The Community
            </h2>
            <p className="text-xl text-gray-400 mb-12">
              Hop in and be part of the revolution!
            </p>
            <div className="flex flex-wrap justify-center gap-4 mb-16" style={{ opacity: visibleSections.has('community') ? 1 : 0, transform: visibleSections.has('community') ? 'translateY(0)' : 'translateY(30px)', transition: 'all 0.8s ease-out 0.3s' }}>
              <a
                href="https://x.com/RocketBunny_SOL"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 px-8 py-4 border-2 text-white font-bold text-lg rounded hover:scale-110 transition-all duration-300 relative group"
                style={{ background: 'linear-gradient(135deg, #DC2626 0%, #F97316 100%)', borderColor: 'transparent', boxShadow: '0 0 30px rgba(239, 68, 68, 0.5)' }}
              >
                <span className="text-2xl font-bold group-hover:rotate-12 transition-transform duration-300" style={{ fontFamily: 'Arial, sans-serif' }}>𝕏</span>
                <span>X.com</span>
              </a>
              <a
                href="https://t.me/rocketbunnysol"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 px-8 py-4 border-2 text-white font-bold text-lg rounded hover:scale-110 transition-all duration-300 relative group"
                style={{ background: 'linear-gradient(135deg, #DC2626 0%, #F97316 100%)', borderColor: 'transparent', boxShadow: '0 0 30px rgba(239, 68, 68, 0.4)' }}
              >
                <Send className="w-6 h-6 group-hover:rotate-12 transition-transform duration-300" />
                <span>Telegram</span>
              </a>
            </div>
            <div className="flex justify-center">
              <img
                src="https://static.lumi.new/ea/ea09fcb60e86f41c973975c0e710af39.webp"
                alt="Rocket Bunny on Moon"
                className="h-64 w-auto hover:scale-105 transition-transform duration-300"
              />
            </div>
          </div>
        </section>

        {/* Moonmap Modal */}
        {showMoonmapModal && (
          <div
            className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4"
            onClick={() => setShowMoonmapModal(false)}
            style={{ backdropFilter: 'blur(8px)' }}
          >
            <div
              className="relative max-w-4xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setShowMoonmapModal(false)}
                className="absolute -top-12 right-0 text-white hover:text-red-500 text-4xl font-bold transition-colors duration-300"
                aria-label="Close modal"
              >
                ×
              </button>
              <img
                src="https://static.lumi.new/31/3155e21147b6e223795162f94bf044d6.webp"
                alt="Rocket Bunny Moonmap"
                className="w-full h-auto rounded-lg border-4 border-red-500"
                style={{ boxShadow: '0 0 60px rgba(239, 68, 68, 0.6)' }}
              />
            </div>
          </div>
        )}

        {/* Footer */}
        <footer className="py-8 px-4 border-t border-red-600/30">
          <div className="max-w-7xl mx-auto text-center text-gray-500">
            <p>© 2026 Rocket Bunny. All rights reserved. To the moon! 🚀🐰</p>
          </div>
        </footer>
      </div>
    </div>
  )
}

export default Home
